<script lang="ts" setup>
import type { IntegrationLoginProvider, SocialLoginProvider } from '~kui/types/auth';
import type { ResponseResult } from '~kui/types/formResult';

import { defineField, defineFormConfig } from '~kui/components/KUI/Core/Form/util/form';
import KUIUnifiedAuthSocialButtons from '~kui/components/KUI/Unified/Auth/KUIUnifiedAuthSocialButtons.vue';
import { isSocialLoginProvider } from '~kui/utils/auth';
import { useI18n } from 'vue-i18n';
import { z } from 'zod';

interface Props {
  initialEmail?: string;
  result?: ResponseResult | null;
  loading?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  initialEmail: '',
  result: null,
  loading: false,
});

const emit = defineEmits<{
  submit: [email: string];
  socialLogin: [provider: SocialLoginProvider];
}>();

const { t } = useI18n();

const formConfig = defineFormConfig(
  {
    email: defineField({
      schema: z.email(t('general.formFields.email.validation')),
      type: 'text',
      options: {
        label: t('general.formFields.email.label'),
        placeholder: t('general.formFields.email.placeholder'),
        required: true,
        dataTestId: 'onboarding-email-field',
        type: 'email',
        autocomplete: 'email',
      },
      initialValue: props.initialEmail,
    }),
  },
  t('general.auth.submitButton.getStarted'),
);

// Onboarding lists no integration buttons (a rostered student is created by the roster, never
// by signup), so the guard only narrows the type the shared buttons emit.
const handleSocialLogin = (provider: SocialLoginProvider | IntegrationLoginProvider) => {
  if (isSocialLoginProvider(provider)) {
    emit('socialLogin', provider);
  }
};

const handleSubmit = (value: z.infer<typeof formConfig.schema>) => {
  emit('submit', value.email);
};
</script>

<template>
  <div>
    <KUICoreForm
      :form-config="formConfig"
      :form-result="result"
      :loading="loading"
      @submit="handleSubmit"
    />

    <KUIUnifiedAuthSocialButtons
      :disabled="loading"
      @social-login="handleSocialLogin"
    />
  </div>
</template>
